import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Privacy - Public privacy policy page.
 *
 * Intentionally NOT wrapped in ProtectedRoute: this page must render for
 * logged-out visitors (e.g. Apple's App Review team during TestFlight
 * review) without redirecting to the Cognito login flow.
 */
export default function Privacy() {
  return (
    <div className="privacy-page">
      <div className="privacy-page-inner">
        <header className="privacy-page-header">
          <Link to="/" className="privacy-page-brand">
            <div className="privacy-page-mark">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <path d="M21 15l-5-5L5 21" />
              </svg>
            </div>
            <span>Collections</span>
          </Link>
        </header>

        <main className="privacy-page-content">
          <h1>Privacy Policy</h1>
          <p className="privacy-page-updated">Last updated: August 18, 2026</p>

          <section className="privacy-page-alert">
            <strong>Collections is an alpha, pre-release product.</strong> It is
            operated by Norimasu for a small group of early users and testers.
            The practices described on this page reflect our current intent
            for the alpha and may evolve as the product and our data
            practices mature — we'll update this policy, and the date above,
            whenever we make a material change.
          </section>

          <section>
            <h2>What we collect</h2>
            <p>To provide the service, we collect and store:</p>
            <ul>
              <li>
                <strong>Account information</strong> — your account email
                address, used for sign-in.
              </li>
              <li>
                <strong>Content you save</strong> — the screenshots, images,
                URLs, and other content you choose to add to your collection.
              </li>
              <li>
                <strong>Derived analysis metadata</strong> — information our AI
                pipeline generates about what you save, such as headlines,
                categories, extracted entities, and vector embeddings used to
                make your collection searchable.
              </li>
            </ul>
            <p>
              We aim to collect only what's needed to run the app, and don't
              collect content beyond what you've chosen to save.
            </p>
          </section>

          <section>
            <h2>Where your data is stored</h2>
            <p>
              Our core application data — your account information, saved
              content, and derived analysis — is stored on cloud
              infrastructure located in the United States, and we intend to
              keep it there. Some third-party providers we use to process
              your content (see below) may operate or transfer data outside
              the United States under their own data-handling terms.
            </p>
          </section>

          <section>
            <h2>Third-party processing</h2>
            <p>
              Saved content may be processed by third-party service providers
              to help analyze and enrich what you save (for example,
              generating descriptions and making content searchable). These
              providers process content under their own standard terms, not
              exclusively ours — and in some cases those terms permit a
              provider to use content we send it to improve that provider's
              own services. We're reviewing each provider's terms and
              settings, and we'll update this policy as we confirm or change
              what's in place.
            </p>
          </section>

          <section>
            <h2>No ads, no selling your data</h2>
            <p>
              Collections does not currently run advertising, and we do not
              sell, rent, or share your personal data or saved content with
              third parties for marketing or advertising purposes. We have no
              plans to change this.
            </p>
          </section>

          <section>
            <h2>Data deletion</h2>
            <p>
              On request during the alpha, we will delete your account and
              remove or de-identify your saved content and derived data.
            </p>
          </section>

          <section>
            <h2>Changes to this policy</h2>
            <p>
              We'll reflect any changes to this policy by updating the "Last
              updated" date at the top of this page.
            </p>
          </section>

          <div className="privacy-page-back">
            <Link to="/">← Back to Collections</Link>
          </div>
        </main>
      </div>
    </div>
  );
}
